// The button. Sturdy border, a hard drop that compresses on press, a bouncy
// hover lift (kit-sheet `.btn`, screen-mock `.btn`).
//
// **The resting fill is translucent**, which is screen-mock's `#2d2e36d9` and
// not kit-sheet's opaque `--panel-2`: these buttons float over the board, and
// an opaque chip on a floating row reads as a hole punched in the scene. The
// filled variants stay opaque — a brand button is the loud one on purpose.
//
// **`icon` is the icon-only form** (§29 U4): equal padding and a fixed square
// the same height as a text button beside it. It lives here rather than as a
// `styled(Btn)` at each call site, because the height it has to match is this
// file's own arithmetic — the turn row had already grown one such wrapper
// (`PauseBtn`) before there were seven of them.
//
// Hover moves the border only on `default` and `ghost`. In the sheet a filled
// variant's `border-color` rule outranks the hover rule by source order, so
// primary/cream/danger keep their own edge while lifting; that is deliberate
// and reproduced here rather than reverse-engineered at each call site.

import { styled } from "@mui/material/styles";

import { motion } from "./motion";

export type BtnVariant = "default" | "primary" | "cream" | "danger" | "ghost";

export const Btn = styled("button", {
  shouldForwardProp: (prop: string) =>
    prop !== "variant" && prop !== "small" && prop !== "icon"
})<{ variant?: BtnVariant; small?: boolean; icon?: boolean }>(
  ({ theme, variant = "default", small, icon: iconOnly }) => {
    const c = theme.kit.color;
    const ghost = variant === "ghost";

    // Fill, edge and text per variant. `undefined` on a hover entry means
    // "keep what the resting state said".
    const skin = {
      default: {
        background: `color-mix(in srgb, ${c.panelRaised} 85%, transparent)`,
        borderColor: c.line,
        color: c.ink,
        hoverBackground: undefined as string | undefined,
        hoverBorder: c.lineHi as string | undefined
      },
      primary: {
        background: c.brand,
        borderColor: c.brandDown,
        color: c.onBrand,
        hoverBackground: c.brandHover as string | undefined,
        hoverBorder: undefined as string | undefined
      },
      cream: {
        background: c.cream,
        borderColor: c.creamEdge,
        color: c.onCream,
        hoverBackground: undefined as string | undefined,
        hoverBorder: undefined as string | undefined
      },
      danger: {
        background: c.rust,
        borderColor: c.rustEdge,
        color: c.onRust,
        hoverBackground: undefined as string | undefined,
        hoverBorder: undefined as string | undefined
      },
      ghost: {
        background: "transparent",
        borderColor: c.line,
        color: c.ink,
        hoverBackground: undefined as string | undefined,
        hoverBorder: c.lineHi as string | undefined
      }
    }[variant];

    return {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.kit.space.xs,
      fontFamily: theme.kit.font.body,
      fontWeight: 700,
      fontSize: small ? 12 : 13.5,
      lineHeight: 1.2,
      color: skin.color,
      background: skin.background,
      border: `2.5px solid ${skin.borderColor}`,
      borderRadius: theme.kit.radius.md,
      // **Icon-only is a square of the same height as the word beside it.**
      // A glyph in a button padded for text sits in a wide lozenge, and a row
      // that mixes the two reads as two rows. The heights are the text
      // button's own arithmetic — 13.5px at 1.2 plus 18 of padding plus 5 of
      // border is 39, and the small one 12 at 1.2 plus 10 plus 5 is 29 — so
      // the two forms line up by construction rather than by a number typed
      // twice. `padding: 0` and a fixed box, because centring is already the
      // flex row above.
      ...(iconOnly
        ? { padding: 0, width: small ? 29 : 39, height: small ? 29 : 39 }
        : { padding: small ? "5px 10px" : "9px 18px" }),
      cursor: "pointer",
      // Ghost has no plate, so it has nothing to cast a shadow.
      boxShadow: ghost ? "none" : `0 2.5px 0 ${c.shadow}`,
      ...motion(["transform"], ["background", "border-color"]),

      "&:hover": {
        transform: "translateY(-2px)",
        ...(skin.hoverBackground ? { background: skin.hoverBackground } : {}),
        ...(skin.hoverBorder ? { borderColor: skin.hoverBorder } : {})
      },
      "&:active": {
        transform: "translateY(1px)",
        boxShadow: ghost ? "none" : `0 1px 0 ${c.shadow}`
      },
      "&:focus-visible": { outline: `2px solid ${c.brand}`, outlineOffset: 2 },
      "&:disabled": { opacity: 0.38, pointerEvents: "none" },

      // The mobile foundation line (PLAN-UI §28.1): a finger needs 44px
      // whatever the type size says. An icon-only button takes it in both
      // directions, or the square becomes a tall slot.
      "@media (pointer: coarse)": {
        minHeight: theme.kit.touchTarget,
        ...(iconOnly ? { minWidth: theme.kit.touchTarget } : {})
      }
    };
  }
);
